/**
 * Foreground TTL auto-dismiss for server-origin (`/v1/send`) notifications.
 *
 * Public-API notifications can carry a `ttl`, mapped to an `expiresAt` epoch on
 * the stored `DecryptedNotification`. The server embeds the hint but does NOT
 * delete on the client's behalf, so the client self-prunes:
 *   - once on mount (catch anything that expired while the tab was closed), and
 *   - on a fixed interval while the tab is open.
 *
 * The web app can run a `setInterval` from the page (unlike the SW, which has no
 * long-lived timer). The prune write goes through `webStorage`, so `useStorage`
 * subscribers (the notification list) re-render automatically and other tabs
 * pick it up via the `reflecto-storage` BroadcastChannel.
 *
 * Device-mirrored (Android) notifications have no `expiresAt` and are untouched,
 * so this is a no-op for users who never receive a public-API send.
 */

import { useEffect } from "react";
import { logWarn } from "../lib/log";
import { pruneExpiredFromStorage } from "../services/notificationsStore";

/**
 * 30s cadence: tight enough that an expired card disappears promptly without the
 * user noticing it lingering, loose enough that the once-every-30s read is
 * negligible. A no-expiry tick reads once and writes nothing.
 */
const PRUNE_INTERVAL_MS = 30_000;

export function useExpiredNotificationPrune(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return;

    const runPrune = () => {
      // Pass the raw error (idiomatic — logWarn/buildPayload serialize err.message
      // internally). Safe here: an IDB prune failure (quota / shutdown) carries no
      // notification content in its message.
      pruneExpiredFromStorage().catch((err) => {
        logWarn("notifications", "prune_expired_failed", err);
      });
    };

    runPrune();
    const id = setInterval(runPrune, PRUNE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [enabled]);
}
